import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
// Components
import { CustomXAxisTick } from './CustomXAxisTick/CustomXAxisTick';
import { CustomYAxisTick } from './CustomYAxisTick/CustomYAxisTick';
import { CustomTooltip } from './CustomTooltip/CustomTooltip';
import { CustomLegend } from './CustomLegend/CustomLegend';

import styles from './DashboardChart.module.scss';

export type DashboardChartData = {
  date: string;
  dayNumber: string;
  merch: number;
  streams: number;
  ama: number;
  tips: number;
  cryptoTips: number;
  offers: number;
  eventMarketing: number;
};

type DashboardChartProps = {
  data: DashboardChartData[];
  currency?: string;
  cryptoCurrency?: string;
  separateBtcAxis?: boolean;
};

const DashboardChart: React.FC<DashboardChartProps> = ({
  data,
  currency = '',
  cryptoCurrency = '₿',
  separateBtcAxis = false,
}) => {
  // Ten revenue streams on one plot. The previous pure-hue web colours put
  // cyan, gold and orange next to each other, which no reader can separate and
  // colour-blind readers see as one line. These ten are ordered so that the
  // closest neighbouring pair measures 19.8 apart in normal vision and 8.4
  // under protanopia, deuteranopia and tritanopia simulated at full severity
  // (OKLab distance x100, Machado et al. matrices, against a light surface).
  // Reordering the series or swapping a colour changes those numbers - measure
  // again rather than assuming the set still separates.
  const experienceColor = '#9c5518';
  const memorabiliaColor = '#0b8fab';
  const merchColor = '#008300';
  const productsColor = '#d55181';
  const streamsColor = '#2a78d6';
  const amaColor = '#e34948';
  const tipsColor = '#4a3aa7';
  const cryptoTipsColor = '#c98500';
  const offersColor = '#199e70';
  const eventMarketingColor = '#eb6834';

  const cryptoYAxisId = separateBtcAxis ? 'btc' : 'fiat';

  return (
    <ResponsiveContainer height={250} className={styles.chartWrapper}>
      <LineChart
        height={250}
        data={data}
        margin={{
          top: 0,
          right: 8,
          left: 8,
          bottom: 0,
        }}
      >
        <CartesianGrid stroke="#eef1f6" horizontal={false} />
        <XAxis
          dataKey="dayNumber"
          stroke="#e4e9f0"
          tick={<CustomXAxisTick />}
          height={50}
        />
        <YAxis
          yAxisId="fiat"
          width={56}
          orientation="left"
          stroke="#e4e9f0"
          allowDecimals={currency !== ''}
          tick={<CustomYAxisTick currency={currency} />}
        />

        {separateBtcAxis && (
          <YAxis
            yAxisId="btc"
            width={56}
            orientation="left"
            dx={40}
            axisLine={false}
            tickLine={false}
            stroke="#e4e9f0"
            tick={<CustomYAxisTick currency={cryptoCurrency} />}
          />
        )}
        <Tooltip
          content={(props) => <CustomTooltip {...props} currency={currency} />}
        />
        <Legend iconType="circle" height={18} content={<CustomLegend />} />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="experience"
          stroke={experienceColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: experienceColor,
            stroke: experienceColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="memorabilia"
          stroke={memorabiliaColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: memorabiliaColor,
            stroke: memorabiliaColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="merch"
          stroke={merchColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: merchColor,
            stroke: merchColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="products"
          stroke={productsColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: productsColor,
            stroke: productsColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="streams"
          stroke={streamsColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: streamsColor,
            stroke: streamsColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="ama"
          stroke={amaColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: amaColor,
            stroke: amaColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="tips"
          stroke={tipsColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: tipsColor,
            stroke: tipsColor,
            strokeWidth: 0,
          }}
        />
        {/* Crypto tips line */}
        <Line
          yAxisId={cryptoYAxisId}
          type="linear"
          dataKey="cryptoTips"
          name="Crypto Tips"
          stroke={cryptoTipsColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: cryptoTipsColor,
            stroke: cryptoTipsColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="offers"
          stroke={offersColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: offersColor,
            stroke: offersColor,
            strokeWidth: 0,
          }}
        />
        <Line
          yAxisId="fiat"
          type="linear"
          dataKey="eventMarketing"
          stroke={eventMarketingColor}
          strokeWidth={2}
          dot={false}
          activeDot={{
            fill: eventMarketingColor,
            stroke: eventMarketingColor,
            strokeWidth: 0,
          }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default DashboardChart;
